import type { MediaModel } from '@components/models';

export const PET_CIRCLE_ASSETS = Object.freeze({
    PROD: 'https://www.petcircle.com.au/petcircle-assets/',
    DEV: 'https://storage.googleapis.com/devtest-petcircle-assets/',
});

export const getFormattedPrice = (price: number, withoutDecimal?: boolean): string => {
    if (withoutDecimal) return `$${price}`;
    return `$${price.toFixed(2)}`;
};

export const ratingNumberToWord = (rating: number) => {
    switch (true) {
        case rating == 5:
            return 'five';
        case rating >= 4.3 && rating < 5:
            return 'four-half';
        case rating >= 3.8 && rating < 4.3:
            return 'four';
        case rating >= 3.3 && rating < 3.8:
            return 'three-half';
        case rating >= 2.8 && rating < 3.3:
            return 'three';
        case rating >= 2.3 && rating < 2.8:
            return 'two-half';
        case rating >= 1.8 && rating < 2.3:
            return 'two';
        case rating >= 1.2 && rating < 1.8:
            return 'one-half';
        case rating >= 0.7 && rating < 1.2:
            return 'one';
        case rating >= 0.1 && rating < 0.7:
            return 'half';
        case rating == 0:
            return 'zero';
        default:
            return 'zero';
    }
};

/**
 * @param url
 */
export const isExternalLink = (url: string) => {
    const urlLink = getLink(url);

    return urlLink.startsWith('http') && !urlLink.includes('petcircle.com.au') && !urlLink.includes('thepaw.com.au');
};

/**
 * @param url_path
 */
export function getLink(url_path: string | undefined) {
    if (url_path) {
        if (url_path.startsWith('http')) {
            // If url contains "petcircle.com.au" or "thepaw.com.au", treat it as a relative path
            if (url_path.includes('petcircle.com.au') || url_path.includes('thepaw.com.au')) {
                // checks if url is not for auto-delivery information page
                if (!url_path.includes('help.petcircle.com.au')) {
                    const tempUrl = url_path.split('com.au');
                    return `${import.meta.env.PUBLIC_BASE_WEBSITE_URL}${tempUrl[1]}`;
                }
            }
            return url_path;
        }
        return `${import.meta.env.PUBLIC_BASE_WEBSITE_URL}${url_path}`;
    }
    return '';
}

export function getSrcset(image: MediaModel): string {
    if (image.formats) {
        return Object.values(image.formats)
            .map((format) => `${format.url} ${format.width}w`)
            .concat([`${image.url} ${image.width}w`])
            .join(', ');
    }
    // There aren't any additional formats so just use the main one.
    return `${image.url} ${image.width}w`;
}

export function styleNames(styles: any) {
    if (!styles || typeof styles !== 'object') {
        return '';
    }

    if (Array.isArray(styles)) {
        return styles.join(';') + ';';
    }

    const styleNames: any = {};
    for (const key of Object.keys(styles)) {
        const value = styles[key];
        const propName = kebabCase(key);
        const shouldContinueLoop =
            typeof value === 'string' || typeof value === 'boolean' || typeof value !== 'object' || !value;

        if (typeof value === 'string' || typeof value === 'boolean') {
            styleNames[propName] = value;
        }

        if (shouldContinueLoop) {
            continue;
        }

        const conditions = styles[key];

        const [styleVal] = Object.keys(conditions).filter(
            (value) =>
                (typeof conditions[value] !== 'function' && conditions[value]) ||
                (typeof conditions[value] === 'function' && conditions[value]())
        );

        if (styleVal) {
            styleNames[propName] = styleVal;
        }
    }

    return styleNames;
}

/**
 *
 * @param {string} subject - subject to kebab
 * @returns {string}
 */
function kebabCase(subject: string) {
    return subject
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/[_\s]/, '-')
        .toLowerCase();
}
